import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import '../edit-car/EditCarModal.css';
import './ChecklistModal.css';
import { getCar } from '../../../api/carsApi';
import { buscarServicosPendentes } from '../../../utils/servicosPendentes';
import {
  createAndCompleteChecklist,
  createChecklistShare,
  deleteChecklistMedia,
  deleteChecklistMediaBatch,
  downloadChecklistPdf,
  getChecklistConfig,
  getChecklistImages,
  getChecklistItems,
  getChecklistSession,
  getChecklistSessionsByCar,
  saveChecklistItems,
  uploadAngleSlot,
  uploadChecklistMedia,
  uploadChecklistVideoDirect,
  uploadCustomSection,
} from '../../../api/checklistsApi';
import { describeRejectedFiles, isPdfFile, isVideoFile, prepareMediaFiles } from '../../../utils/mediaFilePreparation';
import { OBSERVACOES_MAX_LENGTH } from '../../../utils/textLimits';
import type { ChecklistConfig, ChecklistTab, ChecklistType } from '../../../types/checklist';
import { checklistTypeLabel, isServiceVisible } from '../../../types/checklist';
import ChecklistTabs, { type ChecklistTabsStatus } from './ChecklistTabs';
import ComparativoSaidaOriginal from './ComparativoSaidaOriginal';
import { ehFamiliaEntrada, ehFamiliaSaida } from '../../../shared/checklist-familias';
import ComoFunciona, { type ComoFuncionaSecao } from '../../como-funciona/ComoFunciona';
import '../../como-funciona/help-circle-btn.css';
import ConfirmModal from '../confirm/ConfirmModal';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import MediaUploader from './MediaUploader';
import MediaGrid, { type MediaGridItem } from './MediaGrid';
import whatsappIcon from '../../../img/whatsapp.svg';
import ChecklistItemsTab from './ChecklistItemsTab';
import type {
  ChecklistItemAnswer,
  ChecklistItemAnswerDraft,
  ChecklistItemTemplate,
  ItemStatus,
} from '../../../modules/checklist/types/checklistItems';
import { invalidateCarsQueries } from '../../../infrastructure/react-query/invalidateCarsQueries';
import { notify } from '../../../shared/feedback/notify';
import { chaveDoChecklist } from '../../../api/rascunhosApi';
import { useRascunho } from '../../../hooks/useRascunho';
import { useMidiasDoRascunho, type MidiaLocal } from '../../../hooks/useMidiasDoRascunho';
import RascunhoRestaurado from '../../rascunho/RascunhoRestaurado';

interface ChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  carId: string;
  sessionId?: string | null;
  tipoServico?: ChecklistType;
  /** Não estou preenchendo um checklist novo (também usado ao abrir sessão para ver). */
  readOnly?: boolean;
  /**
   * A tela hospedeira permite ENTRAR em modo de edição? (at-65, BUG-V11)
   *
   * Default `true` para não mexer nas telas operacionais. Telas de **consulta** passam
   * `false`: Tela de Controle, Checklists Realizados e a aba Entregues. A permissão do
   * usuário (matriz dinâmica do backend) continua valendo por cima disto — as duas
   * precisam liberar.
   */
  permitirEdicao?: boolean;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen?: boolean;
}

/** Teto das observações gerais da sessão — espelha o @MaxLength do backend. */
const OBSERVATION_MAX_LENGTH = OBSERVACOES_MAX_LENGTH;

/** Pré-carrega as imagens para o modal só abrir com as miniaturas prontas. */
const preloadImages = (urls: string[]) =>
  Promise.all(
    urls.map(
      (url) =>
        new Promise<void>((resolve) => {
          if (!url) return resolve();
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = url;
        })
    )
  );

const ChecklistModal: React.FC<ChecklistModalProps> = ({
  isOpen,
  onClose,
  carId,
  sessionId,
  tipoServico,
  readOnly = false,
  permitirEdicao = true,
  sidebarRef,
}) => {
  const queryClient = useQueryClient();
  const modalRef = useRef<HTMLDivElement>(null);
  // Sessão criada neste preenchimento — em caso de falha nos uploads, o retry
  // reaproveita a sessão em vez de criar (e mover o carro) de novo.
  const createdSessionIdRef = useRef<string | null>(null);
  // Respostas originais da sessão (base do diff no modo edição)
  const originalAnswersRef = useRef<Record<string, ChecklistItemAnswerDraft>>({});
  // Observações originais da sessão (base do diff no modo edição — at-50)
  const originalObservationRef = useRef<string>('');
  // Identificador da sessão de edição atual — agrupa todas as operações
  // (itens, uploads e exclusões) em uma única linha na Auditoria.
  const editBatchIdRef = useRef<string | null>(null);
  const checklistType: ChecklistType = (tipoServico || 'ENTRADA') as ChecklistType;

  const [config, setConfig] = useState<ChecklistConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  // at-60: falha ao carregar o conteúdo já registrado (fotos, itens, sessões).
  // Sem isto, um erro de rede/permissão abria o checklist "em branco" como se
  // nada tivesse sido preenchido — e o usuário podia registrar por cima.
  // Enquanto estiver preenchido, o preenchimento e a edição ficam bloqueados.
  const [loadError, setLoadError] = useState<string | null>(null);
  // Garante um único toast por abertura do modal, mesmo quando várias chamadas
  // falham juntas (ex: 403 atinge todas). O aviso na tela é o sinal principal.
  const loadErrorNotifiedRef = useRef(false);

  const [activeTab, setActiveTab] = useState<ChecklistTab>('ANGLES');

  const [anglesLocalBySlot, setAnglesLocalBySlot] = useState<Record<string, File[]>>({});
  const [anglesUploadedBySlot, setAnglesUploadedBySlot] = useState<Record<string, any[]>>({});
  // Mídias já enviadas que o usuário pediu para "Trocar": só são excluídas do
  // servidor depois que as novas subirem — falha/abandono não perde as antigas.
  const [pendingReplaceBySlot, setPendingReplaceBySlot] = useState<Record<string, any[]>>({});

  // Seções personalizadas de fotos criadas pelo usuário (nome livre, ex: "Pneus")
  const [customLocalBySection, setCustomLocalBySection] = useState<Record<string, File[]>>({});
  const [customUploadedBySection, setCustomUploadedBySection] = useState<Record<string, any[]>>({});
  const [newSectionName, setNewSectionName] = useState('');

  const [serviceLocalFiles, setServiceLocalFiles] = useState<File[]>([]);
  const [serviceUploaded, setServiceUploaded] = useState<any[]>([]);

  // Tipo real da sessão em visualização (pode divergir da prop tipoServico)
  const [viewedType, setViewedType] = useState<ChecklistType | null>(null);
  // SAÍDA não pode ser iniciada sem checklist de entrada concluído
  const [entradaMissing, setEntradaMissing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(undefined);
  const [uploadMessage, setUploadMessage] = useState<string>('Salvando checklist...');
  const [error, setError] = useState<string | null>(null);
  const [carInfo, setCarInfo] = useState<any>(null);
  const [itemTemplates, setItemTemplates] = useState<ChecklistItemTemplate[]>([]);
  const [itemAnswers, setItemAnswers] = useState<Record<string, ChecklistItemAnswerDraft>>({});
  const [hasDamages, setHasDamages] = useState(false);
  // at-50: observações gerais da sessão em foco (preenchimento, visualização ou edição)
  const [observation, setObservation] = useState('');
  // Observações da ENTRADA quando o contexto é de saída — referência somente leitura
  const [entradaObservation, setEntradaObservation] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  // at-58: ajuda embutida ("Como Funciona") do checklist
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  // at-58: exclusão em massa — as thumbs têm caixa de seleção sempre visível
  // (sem modo a ativar, decisão do usuário em 06/08/2026); marcou, a barra de
  // ação aparece e uma confirmação exclui tudo.
  const [selectedMediaIds, setSelectedMediaIds] = useState<Set<string>>(new Set());
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; message: string; confirmLabel?: string; onConfirm: () => void }>({
    isOpen: false,
    message: '',
    onConfirm: () => { },
  });

  // Sessão existente aberta para visualização — pode virar edição conforme permissões
  const isViewingSession = Boolean(sessionId);
  const canEditItems = Boolean(config?.permissions?.canEditItems);
  const canEditMedia = Boolean(config?.permissions?.canEditMedia);
  /**
   * at-65 (BUG-V11): a edição passou a ter DUAS camadas, e as duas precisam liberar.
   *
   *  - a **permissão** (matriz dinâmica do backend) diz QUEM pode editar;
   *  - `permitirEdicao` diz DE ONDE faz sentido editar.
   *
   * Antes, a Tela de Controle abria os checklists com `readOnly` mas o botão "Editar
   * checklist" aparecia normalmente, porque a condição só olhava `isViewingSession` e as
   * permissões — um ADMIN alterava vistoria a partir de uma tela de **consulta**, por efeito
   * colateral. A edição vive nas telas operacionais (Recepção, Chefe de Oficina).
   *
   * ⚠️ NÃO dá para usar `readOnly` aqui: nesta base, `readOnly` significa "não estou
   * preenchendo um checklist novo", e a **Recepção** também o liga ao abrir uma sessão para
   * visualizar (`recepcao.tsx`, `openChecklistReadOnly`). Reaproveitá-lo tiraria o botão de
   * editar de uma tela operacional — a prop precisa ser separada, com semântica própria.
   */
  const canEnterEditMode = isViewingSession && permitirEdicao && (canEditItems || canEditMedia);

  const itemsDisabled = isViewingSession ? !(isEditing && canEditItems) : readOnly;
  const mediaDisabled = isViewingSession ? !(isEditing && canEditMedia) : readOnly;

  // Contexto de SAÍDA: a entrada é exibida como referência somente leitura;
  // apenas as "Imagens de saída" são editáveis.
  const effectiveType: ChecklistType = isViewingSession ? (viewedType || checklistType) : checklistType;
  // at-96: por FAMÍLIA. Com `=== 'SAIDA'`, a saída de um pós-evento cairia no contexto de
  // ENTRADA — o modal deixaria os itens editáveis, esconderia as imagens de saída e rotularia a
  // observação como "de Entrada", tudo sem erro nenhum.
  const isSaidaContext = ehFamiliaSaida(effectiveType);
  const entradaMediaDisabled = isSaidaContext ? true : mediaDisabled;
  const entradaItemsDisabled = isSaidaContext ? true : itemsDisabled;
  const serviceMediaDisabled = isSaidaContext ? mediaDisabled : true;

  // at-50: a observação segue a permissão de ITENS (o backend valida a edição por
  // EDITAR_ITENS_SESSAO), inclusive na saída — onde as mídias seguem outra permissão.
  const observationDisabled = isViewingSession ? !(isEditing && canEditItems) : readOnly;
  const observationLabel = isSaidaContext ? 'Observações de Saída' : 'Observações de Entrada';

  // Memorizado para não gerar um array novo a cada render — os useMemo de contagem
  // de ângulos dependem desta referência.
  const angleConfigs = useMemo(() => config?.angles ?? [], [config]);

  /** editBatchId ativo — só existe durante a edição de uma sessão concluída. */
  const currentEditBatchId = () => (isEditing ? editBatchIdRef.current ?? undefined : undefined);

  const openConfirm = (message: string, onConfirm: () => void, confirmLabel?: string) => {
    setConfirmModal({ isOpen: true, message, confirmLabel, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  /** Fechar o modal durante o preenchimento/edição pede confirmação de abandono. */
  const requestClose = () => {
    if (loading) return;

    // Modal bloqueado (saída sem entrada) não tem nada preenchido para perder
    const isFilling = ((!readOnly && !isViewingSession) || isEditing) && !entradaMissing;
    if (!isFilling) {
      onClose();
      return;
    }

    // O checklist já foi salvo (sessão criada), mas há mídias que não subiram:
    // a mensagem genérica de abandono mentiria — os itens JÁ estão salvos e o
    // checklist da família entrada não pode ser refeito neste atendimento.
    if (createdSessionIdRef.current) {
      openConfirm(
        'O checklist já foi salvo, mas existem mídias que ainda não foram enviadas. Se fechar agora, essas mídias serão descartadas — tente novamente pelo botão Salvar antes de fechar. Fechar mesmo assim?',
        () => {
          closeConfirm();
          if (preenchendo) void descartarRascunhoDoChecklist();
          onClose();
        },
        'Fechar e descartar mídias',
      );
      return;
    }

    openConfirm(
      'Tem certeza de que deseja abandonar o checklist? As informações preenchidas não serão salvas.',
      () => {
        closeConfirm();
        // Abandonar é decisão explícita: o rascunho sai junto, senão voltaria na próxima abertura.
        if (preenchendo) void descartarRascunhoDoChecklist();
        onClose();
      },
      'Abandonar',
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // O modal de confirmação e o drawer de ajuda (at-58) são renderizados
      // FORA do modalRef: sem esta guarda, clicar dentro deles contaria como
      // "clicou fora" e pediria o abandono do checklist.
      //
      // at-72: o drawer de ajuda passou a fechar com Esc. Se algum dia ESTE modal também
      // ganhar fechamento por Esc, a guarda acima precisa valer lá igualmente (somar
      // `isHelpOpen`) — os dois listeners vivem em `document`, são irmãos, e
      // `stopPropagation` não impede que um Esc destinado ao drawer chegue aqui e peça
      // o abandono do preenchimento.
      if (confirmModal.isOpen || isHelpOpen) return;

      const clickedNode = event.target as Node;
      const isOutsideModal = !!modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = !sidebarRef?.current || !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        requestClose();
      }
    };

    if (!isOpen) return;

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose, sidebarRef, confirmModal.isOpen, isHelpOpen, isEditing, loading, readOnly, isViewingSession]);

  useEffect(() => {
    if (!isOpen) return;

    setActiveTab('ANGLES');
    setAnglesLocalBySlot({});
    setAnglesUploadedBySlot({});
    setPendingReplaceBySlot({});
    setCustomLocalBySection({});
    setCustomUploadedBySection({});
    setNewSectionName('');
    setServiceLocalFiles([]);
    setServiceUploaded([]);
    setViewedType(null);
    setEntradaMissing(false);
    setItemTemplates([]);
    setItemAnswers({});
    setError(null);
    setConfigError(null);
    setLoadError(null);
    loadErrorNotifiedRef.current = false;
    setHasDamages(false);
    setIsEditing(false);
    setIsHelpOpen(false);
    setSelectedMediaIds(new Set());
    createdSessionIdRef.current = null;
    originalAnswersRef.current = {};
    editBatchIdRef.current = null;
    setIsInitialLoading(true);

    /**
     * at-60: falha de carga do conteúdo já registrado nunca pode passar em
     * silêncio — a tela ficaria idêntica a "não há nada preenchido". Além do
     * aviso, bloqueia o preenchimento e a edição até a página ser recarregada.
     */
    const reportLoadFailure = (error: unknown, coisa: string) => {
      setLoadError(
        `Não foi possível carregar ${coisa}. O checklist pode estar sendo exibido incompleto: recarregue a página antes de preencher ou editar, para não registrar informação por cima do que já existe. Se persistir, acione a TI.`
      );
      if (loadErrorNotifiedRef.current) return;
      loadErrorNotifiedRef.current = true;
      notify.errorFrom(error, `Não foi possível carregar ${coisa}. Recarregue a página; se persistir, acione a TI.`);
    };

    const loadTasks: Promise<unknown>[] = [];

    loadTasks.push(getChecklistConfig()
      .then((cfg) => {
        setConfig(cfg);

        // Checklist novo (exceto SAÍDA, cujos itens vêm da entrada como referência):
        // templates de itens vêm da configuração ativa
        if (!sessionId && !ehFamiliaSaida(checklistType)) {
          const templates: ChecklistItemTemplate[] = cfg.categories.flatMap((category) =>
            category.items.map((item) => ({
              key: item.key,
              label: item.label,
              category: category.key,
              categoryLabel: category.label,
              order: item.order,
            }))
          );
          const drafts: Record<string, ChecklistItemAnswerDraft> = {};
          for (const template of templates) {
            drafts[template.key] = { status: null, note: '' };
          }
          setItemTemplates(templates);
          setItemAnswers(drafts);
        }
      })
      .catch((e: any) => {
        setConfigError(e?.message || 'Não foi possível carregar a configuração do checklist.');
      }));

    if (carId) {
      loadTasks.push(getCar(carId)
        .then((data) => setCarInfo(data))
        .catch((error) => {
          // Não bloqueia o checklist (só alimenta placa/telefone no cabeçalho),
          // mas o usuário precisa saber por que os dados do veículo sumiram.
          setCarInfo(null);
          notify.errorFrom(error, 'Não foi possível carregar os dados do veículo. Recarregue a página; se persistir, acione a TI.');
        }));
    }

    /** Divide as mídias de uma sessão em ângulos, seções personalizadas e imagens de saída. */
    const splitMedia = (media: any[]) => {
      const byAngleSlot: Record<string, any[]> = {};
      const byCustom: Record<string, any[]> = {};
      const service: any[] = [];

      for (const m of media) {
        if (m?.folder === 'ANGULO') {
          const slotKey = String(m?.slot?.key || '').toUpperCase() || 'SEM_SLOT';
          if (slotKey.startsWith('CUSTOM_')) {
            const name = m?.slot?.label || slotKey;
            if (!byCustom[name]) byCustom[name] = [];
            byCustom[name].push(m);
          } else {
            if (!byAngleSlot[slotKey]) byAngleSlot[slotKey] = [];
            byAngleSlot[slotKey].push(m);
          }
          continue;
        }
        if (m?.folder === 'SERVICO') service.push(m);
      }

      return { byAngleSlot, byCustom, service };
    };

    const preloadOf = (media: any[]) =>
      preloadImages(
        media
          .filter((m: any) => m?.type !== 'VIDEO' && typeof m?.url === 'string')
          .map((m: any) => m.url)
      );

    /** Carrega mídias + itens da sessão de entrada (própria ou referência da saída). */
    const loadEntradaContent = async (entradaSessionId: string) => {
      const [mediaData, itemsData] = await Promise.all([
        getChecklistImages(entradaSessionId).catch((error) => {
          reportLoadFailure(error, 'as fotos já registradas no checklist');
          return [];
        }),
        getChecklistItems(entradaSessionId).catch((error) => {
          reportLoadFailure(error, 'os itens já respondidos do checklist');
          return null;
        }),
      ]);

      const media = Array.isArray(mediaData) ? mediaData : [];
      const { byAngleSlot, byCustom, service } = splitMedia(media);
      setAnglesUploadedBySlot(byAngleSlot);
      setCustomUploadedBySection(byCustom);
      if (service.length > 0) setServiceUploaded((prev) => [...prev, ...service]);

      if (itemsData) {
        const templates = Array.isArray(itemsData?.templates) ? (itemsData.templates as ChecklistItemTemplate[]) : [];
        const answersFromApi = Array.isArray(itemsData?.answers) ? itemsData.answers : [];
        const drafts: Record<string, ChecklistItemAnswerDraft> = {};

        for (const template of templates) {
          drafts[template.key] = { status: null, note: '' };
        }
        for (const answer of answersFromApi) {
          if (!drafts[answer.templateKey]) continue;
          drafts[answer.templateKey] = {
            status: answer.status,
            note: answer.note || '',
          };
        }

        setItemTemplates(templates);
        setItemAnswers(drafts);
        originalAnswersRef.current = JSON.parse(JSON.stringify(drafts));
      }

      await preloadOf(media);
    };

    /** Carrega as mídias da sessão de saída (só imagens de saída). */
    const loadSaidaMedia = async (saidaSessionId: string) => {
      const mediaData = await getChecklistImages(saidaSessionId).catch((error) => {
        reportLoadFailure(error, 'as imagens de saída já registradas');
        return [];
      });
      const media = Array.isArray(mediaData) ? mediaData : [];
      const { service } = splitMedia(media);
      setServiceUploaded((prev) => [...prev, ...service]);
      await preloadOf(media);
    };

    /**
     * Localiza a entrada do atendimento e a carrega como referência somente leitura.
     *
     * at-64 (BUG-V77): devolve TRÊS resultados, não dois. "A consulta respondeu e não há
     * entrada" é conclusão de negócio; "a consulta falhou" não é — e tratá-las igual fazia
     * a tela afirmar que faltava a vistoria de entrada para um atendimento que a tem.
     * O checklist da família entrada não pode ser refeito (1 por atendimento), então essa
     * afirmação errada leva o usuário a refazer o que não deveria.
     */
    const loadEntradaReference = async (): Promise<'ENCONTRADA' | 'AUSENTE' | 'DESCONHECIDA'> => {
      let consultaFalhou = false;
      const sessions = await getChecklistSessionsByCar(carId).catch((error) => {
        // Lista vazia por erro é indistinguível de "não existe checklist de
        // entrada" — sem aviso, a saída acusaria falta de entrada que existe.
        reportLoadFailure(error, 'os checklists já realizados deste atendimento');
        consultaFalhou = true;
        return [];
      });
      if (consultaFalhou) return 'DESCONHECIDA';

      const entrada = (sessions || []).find((s: any) => ehFamiliaEntrada(s?.type));
      if (!entrada) return 'AUSENTE';

      const entradaSession = await getChecklistSession(entrada.id).catch((error) => {
        reportLoadFailure(error, 'o checklist de entrada deste atendimento');
        return null;
      });
      setHasDamages(Boolean(entradaSession?.hasDamages));
      // Observações da entrada entram como referência somente leitura (at-50)
      setEntradaObservation(entradaSession?.observation || '');
      await loadEntradaContent(entrada.id);
      return 'ENCONTRADA';
    };

    if (sessionId) {
      loadTasks.push(
        getChecklistSession(sessionId)
          .then(async (session) => {
            const type = (session?.type || 'ENTRADA') as ChecklistType;
            setViewedType(type);

            // at-50: observações da sessão em foco (base do diff da edição)
            const sessionObservation = session?.observation || '';
            setObservation(sessionObservation);
            originalObservationRef.current = sessionObservation;

            if (ehFamiliaSaida(type)) {
              // Visualização unificada: imagens de saída + entrada como referência
              await Promise.all([loadSaidaMedia(sessionId), loadEntradaReference()]);
            } else {
              setHasDamages(Boolean(session?.hasDamages));
              await loadEntradaContent(sessionId);
            }
          })
          .catch((error) => {
            setAnglesUploadedBySlot({});
            setCustomUploadedBySection({});
            setServiceUploaded([]);
            reportLoadFailure(error, 'o checklist selecionado');
          })
      );
    } else if (ehFamiliaSaida(checklistType)) {
      // Preenchimento de SAÍDA: exibe a entrada concluída em somente leitura.
      // Sem entrada concluída, o checklist de saída não pode ser iniciado.
      loadTasks.push(
        loadEntradaReference()
          // Só afirma a ausência quando a consulta respondeu. Em falha, quem explica é a
          // faixa de `loadError`, e os botões de salvar já ficam travados por ela
          // (`saveBlockedByLoadError`) — a saída não é criada às cegas.
          .then((resultado) => setEntradaMissing(resultado === 'AUSENTE'))
          .catch((error) => {
            reportLoadFailure(error, 'os checklists já realizados deste atendimento');
            setEntradaMissing(false);
          })
      );
    }

    void Promise.allSettled(loadTasks).then(() => setIsInitialLoading(false));
  }, [isOpen, carId, sessionId, checklistType]);

  // ------------------------------------------------------------------ oficina/at-136: rascunho
  //
  // A versão nova do sistema recarrega a página sozinha; o checklist em preenchimento sobrevive.
  // O texto (respostas, observação, avarias, seções criadas) vai para o servidor; as fotos ainda
  // não enviadas ficam neste navegador (IndexedDB) — não cabem no banco.
  //
  // 🔴 `createdSessionId` entra no rascunho: quando o salvar criou a sessão e as mídias falharam,
  // o retry pós-recarga precisa reaproveitá-la. Criar outra seria recusado (1 entrada por
  // atendimento) e o usuário ficaria sem saída.
  const preenchendo = isOpen && !readOnly && !sessionId && Boolean(carId);
  const chaveRascunho = preenchendo ? chaveDoChecklist(carId, checklistType) : null;
  const rascunho = useRascunho<Record<string, unknown>>(chaveRascunho, preenchendo);
  const midiasDoRascunho = useMidiasDoRascunho(chaveRascunho);
  /** Liga a gravação só depois de a restauração acontecer (ou de saber que não havia o que restaurar). */
  const [rascunhoAplicado, setRascunhoAplicado] = useState(false);
  const [rascunhoRestauradoEm, setRascunhoRestauradoEm] = useState<string | null>(null);

  useEffect(() => {
    setRascunhoAplicado(false);
    setRascunhoRestauradoEm(null);
  }, [isOpen, chaveRascunho]);

  // Restaura quando a leitura do rascunho E a carga da tela terminaram — antes disso a carga dos
  // templates sobrescreveria as respostas restauradas com respostas vazias.
  useEffect(() => {
    if (!preenchendo || rascunhoAplicado || isInitialLoading) return;
    if (rascunho.estado === 'carregando' || rascunho.estado === 'inativo') return;
    let vivo = true;
    (async () => {
      const r = rascunho.inicial as any;
      const midias = await midiasDoRascunho.restaurar();
      if (!vivo) return;
      if (r) {
        if (r.itemAnswers && typeof r.itemAnswers === 'object') {
          // Só os itens que ainda existem na configuração; item novo fica sem resposta.
          setItemAnswers((atual) => {
            const proximo = { ...atual };
            for (const chave of Object.keys(atual)) {
              if (r.itemAnswers[chave]) proximo[chave] = r.itemAnswers[chave];
            }
            return proximo;
          });
        }
        setHasDamages(Boolean(r.hasDamages));
        setObservation(typeof r.observation === 'string' ? r.observation : '');
        if (typeof r.createdSessionId === 'string' && r.createdSessionId) createdSessionIdRef.current = r.createdSessionId;
        const secoes: string[] = Array.isArray(r.secoes) ? r.secoes : [];
        if (secoes.length) {
          setCustomLocalBySection((prev) => {
            const proximo = { ...prev };
            for (const s of secoes) proximo[s] = proximo[s] || [];
            return proximo;
          });
        }
      }
      if (midias.length) {
        const angulos: Record<string, File[]> = {};
        const secoes: Record<string, File[]> = {};
        const saida: File[] = [];
        for (const m of midias) {
          if (m.origem === 'ANGULO') (angulos[m.grupo] = angulos[m.grupo] || []).push(m.file);
          else if (m.origem === 'SECAO') (secoes[m.grupo] = secoes[m.grupo] || []).push(m.file);
          else saida.push(m.file);
        }
        setAnglesLocalBySlot((prev) => ({ ...prev, ...angulos }));
        setCustomLocalBySection((prev) => ({ ...prev, ...secoes }));
        setServiceLocalFiles(saida);
      }
      if (r || midias.length) setRascunhoRestauradoEm(rascunho.atualizadoEm ?? new Date().toISOString());
      setRascunhoAplicado(true);
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preenchendo, rascunhoAplicado, isInitialLoading, rascunho.estado]);

  const algoPreenchido =
    Object.values(itemAnswers).some((a) => Boolean(a?.status) || Boolean(a?.note)) ||
    Boolean(observation.trim()) ||
    hasDamages ||
    Object.keys(customLocalBySection).length > 0 ||
    Object.values(anglesLocalBySlot).some((l) => l.length > 0) ||
    serviceLocalFiles.length > 0;
  const conteudoDoRascunhoJson = JSON.stringify({
    itemAnswers,
    hasDamages,
    observation,
    secoes: Object.keys(customLocalBySection),
    createdSessionId: createdSessionIdRef.current,
  });

  useEffect(() => {
    if (!preenchendo || !rascunhoAplicado || !algoPreenchido) return;
    rascunho.agendar(JSON.parse(conteudoDoRascunhoJson));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preenchendo, rascunhoAplicado, algoPreenchido, conteudoDoRascunhoJson]);

  // As mídias pendentes acompanham a tela: entram ao serem escolhidas, saem ao serem enviadas.
  useEffect(() => {
    if (!preenchendo || !rascunhoAplicado) return;
    const atuais: MidiaLocal[] = [];
    for (const [grupo, files] of Object.entries(anglesLocalBySlot)) for (const file of files) atuais.push({ origem: 'ANGULO', grupo, file });
    for (const [grupo, files] of Object.entries(customLocalBySection)) for (const file of files) atuais.push({ origem: 'SECAO', grupo, file });
    for (const file of serviceLocalFiles) atuais.push({ origem: 'SAIDA', grupo: '', file });
    void midiasDoRascunho.sincronizar(atuais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preenchendo, rascunhoAplicado, anglesLocalBySlot, customLocalBySection, serviceLocalFiles]);

  /** Apaga o rascunho (texto no servidor e fotos no navegador). Salvar ou abandonar chamam isto. */
  const descartarRascunhoDoChecklist = async () => {
    setRascunhoRestauradoEm(null);
    await Promise.all([rascunho.descartar(), midiasDoRascunho.limpar()]);
  };

  /** "Começar do zero": só existe enquanto a sessão não foi criada. */
  const comecarChecklistDoZero = () => {
    void descartarRascunhoDoChecklist();
    setItemAnswers((atual) => {
      const vazio: Record<string, ChecklistItemAnswerDraft> = {};
      for (const chave of Object.keys(atual)) vazio[chave] = { status: null, note: '' };
      return vazio;
    });
    setObservation('');
    setHasDamages(false);
    setAnglesLocalBySlot({});
    setCustomLocalBySection({});
    setServiceLocalFiles([]);
  };

  const anglesCountBySlot = useMemo(() => {
    const out: Record<string, number> = {};
    for (const angle of angleConfigs) {
      out[angle.key] =
        (anglesLocalBySlot[angle.key]?.length || 0) + (anglesUploadedBySlot[angle.key]?.length || 0);
    }
    return out;
  }, [angleConfigs, anglesLocalBySlot, anglesUploadedBySlot]);

  const filledAngleSlots = useMemo(() => {
    return angleConfigs.filter((angle) => (anglesCountBySlot[angle.key] || 0) > 0).length;
  }, [angleConfigs, anglesCountBySlot]);

  const missingAngles = useMemo(
    () => angleConfigs.filter((angle) => (anglesCountBySlot[angle.key] || 0) < 1),
    [angleConfigs, anglesCountBySlot]
  );

  // Fotos/vídeos nunca são obrigatórios (decisão de 07/2026) — apenas os itens
  // continuam obrigatórios nos checklists da família entrada.
  const itemsRequired = !isViewingSession && !ehFamiliaSaida(checklistType);

  const itemPendingCount = useMemo(() => {
    return itemTemplates.filter((template) => !itemAnswers[template.key]?.status).length;
  }, [itemTemplates, itemAnswers]);

  const itemPendingByCategory = useMemo(() => {
    const pending: Record<string, number> = {};
    for (const template of itemTemplates) {
      if (!itemAnswers[template.key]?.status) {
        pending[template.category] = (pending[template.category] || 0) + 1;
      }
    }
    return pending;
  }, [itemTemplates, itemAnswers]);

  // Item válido = item respondido. A observação é opcional em qualquer status (at-34).
  const itemValidCount = useMemo(() => {
    return itemTemplates.filter((template) => Boolean(itemAnswers[template.key]?.status)).length;
  }, [itemTemplates, itemAnswers]);

  const customSectionNames = useMemo(() => {
    const names = new Set<string>([
      ...Object.keys(customUploadedBySection),
      ...Object.keys(customLocalBySection),
    ]);
    return Array.from(names);
  }, [customUploadedBySection, customLocalBySection]);

  const customCount = useMemo(() => {
    let total = 0;
    for (const name of customSectionNames) {
      total += (customUploadedBySection[name]?.length || 0) + (customLocalBySection[name]?.length || 0);
    }
    return total;
  }, [customSectionNames, customUploadedBySection, customLocalBySection]);

  const serviceCount = serviceLocalFiles.length + serviceUploaded.length;

  const tabsStatus: ChecklistTabsStatus = useMemo(() => {
    return {
      ANGLES: {
        required: false,
        ok: true,
        count: filledAngleSlots + customCount,
        countLabel: `${filledAngleSlots}/${angleConfigs.length}`,
      },
      SERVICE: {
        required: false,
        ok: true,
        count: serviceCount,
      },
      ITEMS: {
        required: itemsRequired,
        ok: itemPendingCount === 0,
        count: itemValidCount,
        countLabel: `${itemValidCount}/${itemTemplates.length}`,
      },
    };
  }, [
    itemsRequired,
    filledAngleSlots,
    customCount,
    angleConfigs.length,
    serviceCount,
    itemPendingCount,
    itemValidCount,
    itemTemplates.length,
  ]);

  const validateChecklist = () => {
    // Mídias nunca bloqueiam o salvamento; SAÍDA não tem itens próprios.
    if (ehFamiliaSaida(checklistType) && !isViewingSession) {
      setError(null);
      return true;
    }

    if (itemPendingCount > 0) {
      setActiveTab('ITEMS');
      const msg = `Existem itens sem resposta no checklist: ${itemPendingCount}.`;
      setError(msg);
      notify.error(msg);
      return false;
    }

    setError(null);
    return true;
  };

  /** Confirmação leve: lista o que está sendo salvo sem fotos (não bloqueia). */
  const missingPhotoWarnings = (): string[] => {
    const warnings: string[] = [];
    if (!ehFamiliaSaida(checklistType) && missingAngles.length > 0) {
      warnings.push(`ângulos (${missingAngles.map((angle) => angle.label).join(', ')})`);
    }
    if (ehFamiliaSaida(checklistType) && serviceCount === 0) {
      warnings.push('imagens de saída');
    }
    return warnings;
  };

  /**
   * Prepara os arquivos na seleção (copia para memória, valida tipo/tamanho,
   * converte HEIC/WebP/etc para JPEG) — o upload não depende mais do arquivo
   * continuar existindo no disco (ex: temporários do WhatsApp Desktop).
   *
   * at-58: PDF é aceito aqui e vira uma imagem por página. A conversão é
   * síncrona para o usuário (roda no navegador), então o modal avisa enquanto
   * acontece — com o contador de páginas, porque um PDF de dezenas ou centenas
   * de páginas leva bem mais que alguns segundos e a tela pareceria travada.
   */
  const prepareIncomingFiles = async (files: File[]): Promise<File[]> => {
    const temPdf = files.some((file) => isPdfFile(file));
    if (temPdf) {
      setLoading(true);
      setUploadMessage('Convertendo PDF em imagens...');
    }

    try {
      const { ready, rejected } = await prepareMediaFiles(files, {
        allowVideo: true,
        allowPdf: true,
        onPdfProgress: (page, total) =>
          setUploadMessage(`Convertendo PDF em imagens... página ${page} de ${total}`),
      });
      if (rejected.length > 0) {
        notify.error(`Arquivo(s) não anexado(s) — ${describeRejectedFiles(rejected)}`);
      }
      if (temPdf && ready.length > 0) {
        notify.info(`PDF convertido em ${ready.length} imagem(ns).`);
      }
      return ready;
    } finally {
      if (temPdf) {
        setUploadMessage('Salvando checklist...');
        setLoading(false);
      }
    }
  };

  const addAngleFiles = async (slotKey: string, files: File[]) => {
    if (!files || files.length === 0) return;
    const ready = await prepareIncomingFiles(files);
    if (ready.length === 0) return;
    setAnglesLocalBySlot((prev) => ({
      ...prev,
      [slotKey]: [...(prev[slotKey] || []), ...ready],
    }));
  };

  const addCustomFiles = async (sectionName: string, files: File[]) => {
    if (!files || files.length === 0) return;
    const ready = await prepareIncomingFiles(files);
    if (ready.length === 0) return;
    setCustomLocalBySection((prev) => ({
      ...prev,
      [sectionName]: [...(prev[sectionName] || []), ...ready],
    }));
  };

  const addServiceFiles = async (files: File[]) => {
    if (!files || files.length === 0) return;
    const ready = await prepareIncomingFiles(files);
    if (ready.length === 0) return;
    setServiceLocalFiles((prev) => [...prev, ...ready]);
  };

  const createCustomSection = () => {
    const name = newSectionName.trim();
    if (!name) {
      notify.warning('Informe um nome para a seção (ex: Pneus).');
      return;
    }
    const exists = customSectionNames.some((s) => s.toLowerCase() === name.toLowerCase());
    if (exists) {
      notify.warning('Já existe uma seção com esse nome neste checklist.');
      return;
    }
    setCustomLocalBySection((prev) => ({ ...prev, [name]: prev[name] || [] }));
    setNewSectionName('');
  };

  const replaceAngleFiles = async (slotKey: string, files: File[]) => {
    if (!files || files.length === 0) return;

    const ready = await prepareIncomingFiles(files);
    if (ready.length === 0) return;

    setAnglesLocalBySlot((prev) => ({ ...prev, [slotKey]: ready }));

    // As mídias já enviadas saem da tela agora, mas só são excluídas do
    // servidor depois que as novas subirem (uploadLocalFiles) — se o envio
    // falhar ou o usuário abandonar, as antigas continuam no checklist.
    const existingUploaded = anglesUploadedBySlot[slotKey] || [];
    if (existingUploaded.length > 0) {
      setPendingReplaceBySlot((prev) => ({
        ...prev,
        [slotKey]: [...(prev[slotKey] || []), ...existingUploaded],
      }));
      setAnglesUploadedBySlot((prev) => ({ ...prev, [slotKey]: [] }));
    }
  };

  const countLocalFilesToUpload = () => {
    let total = 0;
    for (const angle of angleConfigs) {
      total += (anglesLocalBySlot[angle.key] || []).length;
    }
    for (const name of Object.keys(customLocalBySection)) {
      total += (customLocalBySection[name] || []).length;
    }
    if (isServiceVisible(checklistType) || isViewingSession) {
      total += serviceLocalFiles.length;
    }
    return total;
  };

  /** Sobe todos os arquivos locais para a sessão, removendo do estado local conforme conclui. */
  const uploadLocalFiles = async (targetSessionId: string) => {
    const totalFilesToUpload = countLocalFilesToUpload();
    let uploadedFilesCount = 0;
    setUploadProgress(totalFilesToUpload > 0 ? 0 : undefined);

    const progressFor = (percent: number) => {
      const baseProgress = (uploadedFilesCount / totalFilesToUpload) * 100;
      const contribution = (1 / totalFilesToUpload) * percent;
      setUploadProgress(Math.round(baseProgress + contribution));
    };

    for (const angle of angleConfigs) {
      const files = anglesLocalBySlot[angle.key] || [];
      for (const file of [...files]) {
        setUploadMessage(`Enviando mídia ${uploadedFilesCount + 1} de ${totalFilesToUpload} (${file.name})...`);
        // Vídeos vão direto ao GCS (URL assinada) — não passam pelo backend
        const created = isVideoFile(file)
          ? [await uploadChecklistVideoDirect(targetSessionId, 'ANGLES', file, { slot: angle.key, editBatchId: currentEditBatchId() }, progressFor)]
          : await uploadAngleSlot(targetSessionId, angle.key, [file], progressFor, currentEditBatchId());
        setAnglesUploadedBySlot((prev) => ({ ...prev, [angle.key]: [...(prev[angle.key] || []), ...created] }));
        setAnglesLocalBySlot((prev) => ({
          ...prev,
          [angle.key]: (prev[angle.key] || []).filter((f) => f !== file),
        }));
        uploadedFilesCount++;
      }

      // "Trocar": as antigas só saem do servidor depois que as novas subiram
      const replaced = pendingReplaceBySlot[angle.key] || [];
      if (files.length > 0 && replaced.length > 0) {
        // at-60: a falha aqui não invalida os envios que acabaram de dar certo,
        // mas não pode passar em silêncio — as mídias antigas continuam no
        // checklist, e a tela já as havia escondido. Volta a exibi-las (é o que
        // o servidor tem) e avisa, em vez de fingir que foram removidas.
        const failedToDelete: any[] = [];
        let firstDeleteError: unknown = null;
        await Promise.all(
          replaced.map(async (m: any) => {
            try {
              await deleteChecklistMedia(String(m.id), currentEditBatchId());
            } catch (error) {
              failedToDelete.push(m);
              if (firstDeleteError === null) firstDeleteError = error;
            }
          })
        );
        if (failedToDelete.length > 0) {
          setAnglesUploadedBySlot((prev) => ({
            ...prev,
            [angle.key]: [...(prev[angle.key] || []), ...failedToDelete],
          }));
          notify.actionError(firstDeleteError, { action: 'delete', subject: 'a mídia substituída' });
        }
        setPendingReplaceBySlot((prev) => ({ ...prev, [angle.key]: [] }));
      }
    }

    for (const sectionName of Object.keys(customLocalBySection)) {
      const files = customLocalBySection[sectionName] || [];
      for (const file of [...files]) {
        setUploadMessage(`Enviando mídia ${uploadedFilesCount + 1} de ${totalFilesToUpload} (${file.name})...`);
        const created = isVideoFile(file)
          ? [await uploadChecklistVideoDirect(targetSessionId, 'CUSTOM', file, { sectionName, editBatchId: currentEditBatchId() }, progressFor)]
          : await uploadCustomSection(targetSessionId, sectionName, [file], progressFor, currentEditBatchId());
        setCustomUploadedBySection((prev) => ({
          ...prev,
          [sectionName]: [...(prev[sectionName] || []), ...created],
        }));
        setCustomLocalBySection((prev) => ({
          ...prev,
          [sectionName]: (prev[sectionName] || []).filter((f) => f !== file),
        }));
        uploadedFilesCount++;
      }
    }

    if (isServiceVisible(checklistType) || isViewingSession) {
      for (const file of [...serviceLocalFiles]) {
        setUploadMessage(`Enviando mídia ${uploadedFilesCount + 1} de ${totalFilesToUpload} (${file.name})...`);
        const created = isVideoFile(file)
          ? [await uploadChecklistVideoDirect(targetSessionId, 'SERVICE', file, { editBatchId: currentEditBatchId() }, progressFor)]
          : await uploadChecklistMedia(targetSessionId, 'SERVICE', [file], progressFor, { editBatchId: currentEditBatchId() });
        setServiceUploaded((prev) => [...prev, ...created]);
        setServiceLocalFiles((prev) => prev.filter((f) => f !== file));
        uploadedFilesCount++;
      }
    }
  };

  const handleSaveChecklist = async () => {
    if (!validateChecklist()) return;

    setLoading(true);
    setError(null);

    try {
      const payloadAnswers: ChecklistItemAnswer[] = itemTemplates.map((template) => {
        const draft = itemAnswers[template.key];
        return {
          templateKey: template.key,
          status: (draft?.status || 'NA') as ChecklistItemAnswer['status'],
          note: draft?.note?.trim() || undefined,
        };
      });

      let newSessionId = createdSessionIdRef.current;
      if (!newSessionId) {
        const created = await createAndCompleteChecklist({
          carId,
          tipoServico: checklistType,
          hasDamages: ehFamiliaEntrada(checklistType) ? hasDamages : undefined,
          observation: observation.trim() || undefined,
          // SAÍDA não tem itens próprios — a entrada permanece como referência
          items: ehFamiliaSaida(checklistType) ? undefined : payloadAnswers,
        });
        newSessionId = created.sessionId;
        createdSessionIdRef.current = newSessionId;
      }

      setUploadMessage('Salvando checklist...');
      await uploadLocalFiles(newSessionId);

      await invalidateCarsQueries(queryClient);

      createdSessionIdRef.current = null;
      // Só depois de sessão e mídias salvas: antes disso o rascunho é o que garante a retomada.
      if (preenchendo) await descartarRascunhoDoChecklist();
      // at-134: concluir a SAÍDA entrega o veículo, e é o momento de lembrar a pesquisa de
      // satisfação — o envio é manual (ADR 0031) e, esquecido, passava em silêncio.
      if (ehFamiliaSaida(checklistType)) {
        notify.success(
          'Veículo entregue. Lembre de enviar a Pesquisa de Satisfação: ela fica na aba Entregues, na placa do veículo.',
          { autoClose: 8000 },
        );
      } else {
        notify.success('Checklist concluído com sucesso!');
      }
      onClose();
    } catch (err: any) {
      const apiMessage = err?.message;
      const message =
        typeof apiMessage === 'string' && apiMessage.trim().length > 0
          ? apiMessage
          : 'Não foi possível salvar o checklist. Tente novamente.';

      setError(message);
      notify.error(message);
    } finally {
      setUploadProgress(undefined);
      setUploadMessage('Salvando checklist...');
      setLoading(false);
    }
  };

  /** Modo edição de sessão concluída: salva só o diff de itens + mídias novas. */
  const handleSaveSessionEdits = async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      if (canEditItems) {
        const changedAnswers: ChecklistItemAnswer[] = [];
        for (const template of itemTemplates) {
          const draft = itemAnswers[template.key];
          const original = originalAnswersRef.current[template.key];
          if (!draft?.status) continue;
          const noteNow = draft.note?.trim() || '';
          const noteBefore = (original?.note || '').trim();
          if (original?.status !== draft.status || noteBefore !== noteNow) {
            changedAnswers.push({
              templateKey: template.key,
              status: draft.status,
              note: noteNow || undefined,
            });
          }
        }

        // at-50: a observação vai no MESMO request dos itens (um só log na Auditoria).
        // Só é enviada quando mudou — ausente, o backend preserva o valor atual.
        const observationNow = observation.trim();
        const observationChanged = observationNow !== originalObservationRef.current.trim();

        if (changedAnswers.length > 0 || observationChanged) {
          await saveChecklistItems(
            sessionId,
            changedAnswers,
            currentEditBatchId(),
            observationChanged ? observationNow : undefined,
          );
          originalAnswersRef.current = JSON.parse(JSON.stringify(itemAnswers));
          originalObservationRef.current = observationNow;
        }
      }

      if (canEditMedia) {
        setUploadMessage('Enviando mídias...');
        await uploadLocalFiles(sessionId);
      }

      notify.success('Checklist atualizado com sucesso!');
      setIsEditing(false);
    } catch (err: any) {
      const message = err?.message || 'Não foi possível salvar as alterações do checklist.';
      setError(message);
      notify.error(message);
    } finally {
      setUploadProgress(undefined);
      setUploadMessage('Salvando checklist...');
      setLoading(false);
    }
  };

  // A observação é independente do status (at-34): trocar o status preserva o que foi digitado.
  const updateItemStatus = (templateKey: string, status: ItemStatus) => {
    setItemAnswers((prev) => {
      const current = prev[templateKey] || { status: null, note: '' };
      return {
        ...prev,
        [templateKey]: {
          status,
          note: current.note,
        },
      };
    });
  };

  const updateItemNote = (templateKey: string, note: string) => {
    setItemAnswers((prev) => ({
      ...prev,
      [templateKey]: {
        status: prev[templateKey]?.status || null,
        note,
      },
    }));
  };

  /** Marca tudo OK preservando as observações já digitadas ("Limpar respostas" é quem zera). */
  const markAllItemsOk = () => {
    setItemAnswers((prev) => {
      const next: Record<string, ChecklistItemAnswerDraft> = {};
      for (const template of itemTemplates) {
        next[template.key] = { status: 'OK', note: prev[template.key]?.note || '' };
      }
      return next;
    });
  };

  const clearAllItems = () => {
    const next: Record<string, ChecklistItemAnswerDraft> = {};
    for (const template of itemTemplates) {
      next[template.key] = { status: null, note: '' };
    }
    setItemAnswers(next);
  };

  const serviceGridItems: MediaGridItem[] = useMemo(() => {
    const uploaded = serviceUploaded.map((m: any) => ({
      key: `uploaded:${m.id}`,
      kind: 'uploaded' as const,
      url: m.url,
      mimeType: m.type === 'VIDEO' ? 'video/*' : 'image/*',
      name: m.storageKey,
      uploadedId: m.id,
    }));

    const locals = serviceLocalFiles.map((file, idx) => ({
      key: `local:${idx}:${file.name}`,
      kind: 'local' as const,
      url: URL.createObjectURL(file),
      mimeType: file.type,
      name: file.name,
    }));

    return [...uploaded, ...locals];
  }, [serviceUploaded, serviceLocalFiles]);

  const removeServiceItem = async (it: MediaGridItem) => {
    if (serviceMediaDisabled) return;

    if (it.kind === 'local') {
      const parts = String(it.key).split(':');
      const index = parts.length >= 2 ? Number(parts[1]) : NaN;

      if (!Number.isNaN(index)) {
        setServiceLocalFiles((prev) => prev.filter((_, i) => i !== index));
      }

      try {
        URL.revokeObjectURL(it.url);
      } catch {
        // noop
      }
      return;
    }

    if (!it.uploadedId) return;

    setLoading(true);
    setError(null);
    try {
      await deleteChecklistMedia(it.uploadedId, currentEditBatchId());
      setServiceUploaded((prev) => prev.filter((m: any) => m.id !== it.uploadedId));
      notify.success('Mídia excluída do checklist.');
    } catch (e: any) {
      setError(e?.message || 'Não foi possível remover a mídia.');
    } finally {
      setLoading(false);
    }
  };

  const customCardItems = (sectionName: string): MediaGridItem[] => {
    const uploaded = (customUploadedBySection[sectionName] || []).map((m: any) => ({
      key: `uploaded:${m.id}`,
      kind: 'uploaded' as const,
      url: m.url,
      mimeType: m.type === 'VIDEO' ? 'video/*' : 'image/*',
      name: m.storageKey,
      uploadedId: m.id,
    }));

    const locals = (customLocalBySection[sectionName] || []).map((f: File, idx: number) => ({
      key: `local:${idx}:${f.name}`,
      kind: 'local' as const,
      url: URL.createObjectURL(f),
      mimeType: f.type,
      name: f.name,
    }));

    return [...uploaded, ...locals];
  };

  const removeCustomItem = async (sectionName: string, it: MediaGridItem) => {
    if (entradaMediaDisabled) return;

    if (it.kind === 'local') {
      const parts = String(it.key).split(':');
      const index = parts.length >= 2 ? Number(parts[1]) : NaN;

      if (!Number.isNaN(index)) {
        setCustomLocalBySection((prev) => ({
          ...prev,
          [sectionName]: (prev[sectionName] || []).filter((_, i) => i !== index),
        }));
      }

      try {
        URL.revokeObjectURL(it.url);
      } catch {
        // noop
      }
      return;
    }

    if (!it.uploadedId) return;

    openConfirm('Remover esta mídia do checklist?', async () => {
      closeConfirm();
      setLoading(true);
      setError(null);
      try {
        await deleteChecklistMedia(it.uploadedId!, currentEditBatchId());
        setCustomUploadedBySection((prev) => ({
          ...prev,
          [sectionName]: (prev[sectionName] || []).filter((m: any) => m.id !== it.uploadedId),
        }));
        notify.success('Mídia excluída do checklist.');
      } catch (e: any) {
        setError(e?.message || 'Não foi possível remover a mídia.');
      } finally {
        setLoading(false);
      }
    });
  };

  /** Baixa o PDF do checklist do atendimento e abre em nova aba (de onde dá para imprimir). */
  const handleDownloadPdf = async () => {
    // A aba precisa ser aberta de forma síncrona (dentro do gesto de clique),
    // senão o bloqueador de pop-up impede o window.open após o await.
    const pdfTab = window.open('', '_blank');
    if (pdfTab) {
      pdfTab.document.write('<title>Gerando PDF do checklist...</title><p style="font-family:sans-serif;padding:24px">Gerando PDF do checklist, aguarde...</p>');
    }

    setLoading(true);
    setUploadMessage('Gerando PDF do checklist...');
    try {
      const blob = await downloadChecklistPdf(carId);
      const url = URL.createObjectURL(blob);

      if (pdfTab) {
        pdfTab.location.href = url;
      } else {
        // Pop-up bloqueado: baixa o arquivo diretamente
        const link = document.createElement('a');
        link.href = url;
        link.download = `checklist-${carInfo?.plate || carId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (e: any) {
      pdfTab?.close();
      notify.error(e?.message || 'Não foi possível gerar o PDF do checklist.');
    } finally {
      setUploadMessage('Salvando checklist...');
      setLoading(false);
    }
  };

  /** Abre o WhatsApp do atendente com a mensagem padrão + link público do checklist. */
  const handleShareWhatsApp = async () => {
    setLoading(true);
    setUploadMessage('Gerando link de compartilhamento...');
    try {
      const share = await createChecklistShare(carId);
      const link = `${window.location.origin}/checklist/${share.token}`;
      const message = share.template
        .replace(/\{\{link\}\}/g, link)
        .replace(/\{\{placa\}\}/g, share.plate || '')
        .replace(/\{\{nome\}\}/g, share.ownerName || 'cliente');

      const digits = String(share.ownerPhone || '').replace(/\D/g, '');
      const phoneWithCountry = digits.startsWith('55') && digits.length >= 12 ? digits : `55${digits}`;
      window.open(`https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
    } catch (e: any) {
      notify.error(e?.message || 'Não foi possível compartilhar o checklist.');
    } finally {
      setUploadMessage('Salvando checklist...');
      setLoading(false);
    }
  };

  const angleCardItems = (slotKey: string): MediaGridItem[] => {
    const uploaded = (anglesUploadedBySlot[slotKey] || []).map((m: any) => ({
      key: `uploaded:${m.id}`,
      kind: 'uploaded' as const,
      url: m.url,
      mimeType: m.type === 'VIDEO' ? 'video/*' : 'image/*',
      name: m.storageKey,
      uploadedId: m.id,
    }));

    const locals = (anglesLocalBySlot[slotKey] || []).map((f: File, idx: number) => ({
      key: `local:${idx}:${f.name}`,
      kind: 'local' as const,
      url: URL.createObjectURL(f),
      mimeType: f.type,
      name: f.name,
    }));

    return [...uploaded, ...locals];
  };

  const removeAngleItem = async (slotKey: string, it: MediaGridItem) => {
    if (mediaDisabled) return;

    if (it.kind === 'local') {
      const parts = String(it.key).split(':');
      const index = parts.length >= 2 ? Number(parts[1]) : NaN;

      if (!Number.isNaN(index)) {
        setAnglesLocalBySlot((prev) => ({
          ...prev,
          [slotKey]: (prev[slotKey] || []).filter((_, i) => i !== index),
        }));
      }

      try {
        URL.revokeObjectURL(it.url);
      } catch {
        // noop
      }
      return;
    }

    if (!it.uploadedId) return;

    openConfirm('Remover esta mídia do checklist?', async () => {
      closeConfirm();
      setLoading(true);
      setError(null);
      try {
        await deleteChecklistMedia(it.uploadedId!, currentEditBatchId());
        setAnglesUploadedBySlot((prev) => ({
          ...prev,
          [slotKey]: (prev[slotKey] || []).filter((m: any) => m.id !== it.uploadedId),
        }));
        notify.success('Mídia excluída do checklist.');
      } catch (e: any) {
        setError(e?.message || 'Não foi possível remover a mídia.');
      } finally {
        setLoading(false);
      }
    });
  };

  // ── Exclusão em massa de mídias (at-58) ───────────────────────────────────
  // A dor real: sem isso, apagar 200 fotos são 200 ciclos de
  // selecionar → confirmar → excluir. Aqui o usuário marca o que quer, confirma
  // uma vez e tudo sai em uma única chamada. A caixa de seleção fica sempre
  // visível nas thumbs (sem modo a ativar — decisão do usuário, 06/08/2026).

  /** Mídias já enviadas de cada origem, com o rótulo usado na confirmação. */
  const gruposDeMidiaEnviada = useMemo(() => {
    const angulos = Object.values(anglesUploadedBySlot).flat();
    const secoes = Object.values(customUploadedBySection).flat();
    return [
      { chave: 'angulos', rotulo: 'de ângulos', midias: angulos, editavel: !entradaMediaDisabled },
      { chave: 'secoes', rotulo: 'de seções personalizadas', midias: secoes, editavel: !entradaMediaDisabled },
      { chave: 'servico', rotulo: 'das imagens de saída', midias: serviceUploaded, editavel: !serviceMediaDisabled },
    ];
  }, [anglesUploadedBySlot, customUploadedBySection, serviceUploaded, entradaMediaDisabled, serviceMediaDisabled]);

  /** Existe ao menos uma mídia enviada em seção que o usuário pode editar? */
  const temMidiaSelecionavel = useMemo(
    () => gruposDeMidiaEnviada.some((grupo) => grupo.editavel && grupo.midias.length > 0),
    [gruposDeMidiaEnviada],
  );

  /** Ids de todas as mídias enviadas no momento — base da poda da seleção. */
  const idsDeMidiaEnviada = useMemo(
    () => new Set(gruposDeMidiaEnviada.flatMap((grupo) => grupo.midias.map((m: any) => String(m.id)))),
    [gruposDeMidiaEnviada],
  );

  // Poda da seleção: mídia que saiu da tela por outro caminho (X individual,
  // "Remover todas do ângulo", "Trocar") não pode continuar contada na barra.
  useEffect(() => {
    setSelectedMediaIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => idsDeMidiaEnviada.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [idsDeMidiaEnviada]);

  const toggleMediaSelection = (item: MediaGridItem) => {
    if (!item.uploadedId) return;
    setSelectedMediaIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.uploadedId as string)) next.delete(item.uploadedId as string);
      else next.add(item.uploadedId as string);
      return next;
    });
  };

  const limparSelecao = () => setSelectedMediaIds(new Set());

  /** "8 de ângulos, 3 de seções personalizadas e 1 das imagens de saída" */
  const descreverSelecao = (): string => {
    const partes = gruposDeMidiaEnviada
      .map((grupo) => ({
        rotulo: grupo.rotulo,
        total: grupo.midias.filter((m: any) => selectedMediaIds.has(String(m.id))).length,
      }))
      .filter((parte) => parte.total > 0)
      .map((parte) => `${parte.total} ${parte.rotulo}`);

    if (partes.length === 0) return '';
    if (partes.length === 1) return partes[0];
    return `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`;
  };

  const handleDeleteSelected = () => {
    const ids = Array.from(selectedMediaIds);
    if (ids.length === 0) return;

    const detalhe = descreverSelecao();
    const auditoria = currentEditBatchId()
      ? ' A exclusão ficará registrada na Auditoria.'
      : '';

    openConfirm(
      `Excluir ${ids.length} mídia(s) do checklist?${detalhe ? ` Serão removidas ${detalhe}.` : ''} Esta ação não pode ser desfeita.${auditoria}`,
      async () => {
        closeConfirm();
        setLoading(true);
        setError(null);
        try {
          const resultado = await deleteChecklistMediaBatch(ids, currentEditBatchId());
          // naoEncontradas também saem da tela: não existem mais no servidor
          // (apagadas em outra aba, por exemplo).
          const removidos = new Set([...resultado.excluidas, ...resultado.naoEncontradas]);
          const semRemovidos = (lista: any[]) => lista.filter((m: any) => !removidos.has(String(m.id)));

          setAnglesUploadedBySlot((prev) => {
            const next: Record<string, any[]> = {};
            Object.entries(prev).forEach(([slot, midias]) => { next[slot] = semRemovidos(midias); });
            return next;
          });
          setCustomUploadedBySection((prev) => {
            const next: Record<string, any[]> = {};
            Object.entries(prev).forEach(([secao, midias]) => { next[secao] = semRemovidos(midias); });
            return next;
          });
          setServiceUploaded((prev) => semRemovidos(prev));

          if (resultado.excluidas.length > 0) {
            notify.success(
              resultado.excluidas.length === 1
                ? 'Mídia excluída do checklist.'
                : `${resultado.excluidas.length} mídias excluídas do checklist.`,
            );
          }
          if (resultado.naoEncontradas.length > 0) {
            notify.info(`${resultado.naoEncontradas.length} mídia(s) já haviam sido removidas.`);
          }
          limparSelecao();
        } catch (e: any) {
          // Seleção preservada de propósito: o usuário pode tentar de novo sem
          // remarcar tudo.
          setError(e?.message || 'Não foi possível remover as mídias selecionadas.');
        } finally {
          setLoading(false);
        }
      },
      `Excluir ${ids.length} mídia(s)`,
    );
  };

  /**
   * Ajuda embutida do checklist (at-58). É a tela mais densa em regra do
   * sistema — família de entrada × saída, permissões independentes de itens e
   * mídias, o que vai parar na Auditoria — e nada disso estava explicado num
   * lugar só. O drawer conta a REGRA, não o clique.
   */
  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que é o checklist',
      conteudo: (
        <>
          <p>
            O checklist é a vistoria do veículo registrada em duas ocasiões: na{' '}
            <strong>entrada</strong> na oficina e na <strong>saída</strong>, quando o veículo é devolvido ao
            cliente. Ele guarda o estado de cada item, as fotos e o que foi combinado — é o que protege a
            oficina e o cliente numa divergência depois da entrega.
          </p>
          <p>
            Cada atendimento aceita <strong>um único checklist de entrada</strong>, que{' '}
            <strong>não pode ser refeito</strong> neste atendimento. A <strong>Saída</strong> só pode ser
            preenchida depois que existir um de entrada concluído, e concluí-la{' '}
            <strong>encerra o atendimento</strong>: o veículo passa para Entrega e some das listagens de
            trabalho, ficando acessível pela aba Entregues.
          </p>
          <p>
            Concluir o checklist de entrada <strong>não move o veículo</strong> nem significa que ele está
            liberado para trabalho — é só a vistoria. Quem define a próxima etapa é o Chefe de Oficina.
          </p>
        </>
      ),
    },
    {
      titulo: 'Pós-evento (retorno de garantia)',
      conteudo: (
        <>
          <p>
            Quando o atendimento é um <strong>pós-evento</strong> — o veículo já foi entregue e voltou para
            avaliação do serviço —, os tipos de checklist são <strong>Entrada de pós-evento</strong> e{' '}
            <strong>Saída de pós-evento</strong>. Os <strong>itens são os mesmos</strong> de sempre; o que muda é
            o rótulo e a ligação com o serviço original.
          </p>
          <p>
            O <strong>tipo não é escolhido</strong>: ele acompanha o atendimento. Um atendimento de pós-evento só
            aceita os checklists de pós-evento, e um atendimento comum só aceita os normais — é o que impede que
            o documento enviado ao cliente descreva a vistoria errada.
          </p>
          <p>
            Na vistoria de <strong>entrada</strong> do pós-evento, a tela oferece o bloco{' '}
            <strong>Como o veículo saiu do serviço anterior</strong>: é a vistoria de saída daquele atendimento,
            em leitura, para comparar como o carro saiu com como ele voltou. Se o serviço original tiver sido
            entregue <strong>sem</strong> checklist de saída, a tela diz isso — e essa mensagem é diferente da de
            falha na consulta, que avisa que não foi possível verificar.
          </p>
          <p>
            Há um terceiro caso: o serviço original pode <strong>não estar no sistema</strong> — o veículo volta em
            garantia de um serviço que não foi feito nesta oficina, ou que é anterior ao sistema. Aí o atendimento
            aparece como <strong>Pós-evento externo</strong> e fica ligado apenas ao número do evento original.
            Não existe atendimento anterior nem vistoria de saída para comparar, e a tela diz isso no lugar do
            bloco de comparação — não é falha de consulta, é ausência de histórico.
          </p>
          <p>
            Para o cliente, no PDF e na tela pública, o rótulo é <strong>retorno de garantia</strong>.
          </p>
        </>
      ),
    },
    {
      titulo: 'Fotos, vídeos e PDF',
      conteudo: (
        <>
          <p>
            <strong>Nenhuma foto é obrigatória</strong> e não há limite de quantidade — anexe quantas
            precisar, por posição do veículo. Além das posições fixas, dá para criar{' '}
            <strong>seções personalizadas</strong> com nome livre (ex: Pneus, Motor), que valem só neste
            checklist.
          </p>
          <p>
            Fotos de iPhone (HEIC) e outros formatos são convertidos automaticamente ao anexar. Um{' '}
            <strong>PDF vira uma imagem por página</strong> — útil quando a digitalização chega nesse
            formato; o limite é de 500 páginas e 200MB por arquivo, e PDF protegido por senha não é
            aceito. A conversão acontece nesta máquina e mostra o número da página enquanto roda —
            arquivo com muitas páginas pode levar alguns minutos.
          </p>
          <p>
            Vídeos são enviados por um caminho próprio, direto para o armazenamento. Se a rede da oficina
            bloquear esse envio, o sistema tenta de novo por outro caminho automaticamente — vídeos acima de
            30MB, nesse cenário, falham e precisam da TI.
          </p>
        </>
      ),
    },
    {
      titulo: 'Excluir várias mídias de uma vez',
      conteudo: (
        <p>
          Cada miniatura já enviada tem uma <strong>caixa de seleção</strong>. Marque quantas quiser — a
          seleção vale para as duas abas de fotos ao mesmo tempo — e use{' '}
          <strong>Excluir selecionadas</strong>: o sistema pede <strong>uma única confirmação</strong>,
          descrevendo quantas mídias saem de cada lugar. O <strong>X</strong> na miniatura continua
          existindo para remover uma só. A exclusão <strong>não pode ser desfeita</strong>; em checklist já
          concluído, ela fica registrada na Auditoria com quem excluiu e quando.
        </p>
      ),
    },
    {
      titulo: 'Itens e observações',
      conteudo: (
        <>
          <p>
            Todos os itens configurados devem ser respondidos no checklist de entrada. A{' '}
            <strong>observação de cada item é opcional em qualquer situação</strong> — inclusive quando o
            item está avariado ou faltando — e nunca impede o salvamento. Trocar o status preserva o texto
            já digitado.
          </p>
          <p>
            Além disso, cada checklist tem um campo de <strong>observações gerais</strong> (Observações de
            Entrada ou de Saída), para o que não cabe nos itens: combinados com o cliente, pendências,
            avisos. Esse texto <strong>aparece no PDF e na tela que o cliente acessa pelo link</strong> —
            escreva pensando nele. Não confunda com as Observações do Cliente e da Oficina do cadastro do
            veículo, que são internas e nunca saem para o cliente.
          </p>
        </>
      ),
    },
    {
      titulo: 'Editar um checklist já concluído',
      conteudo: (
        <>
          <p>
            Checklist concluído pode ser corrigido pelo botão <strong>Editar</strong>, e{' '}
            <strong>toda alteração aparece na Auditoria do veículo</strong>: respostas alteradas (com o
            antes e o depois), mídias adicionadas e mídias excluídas, agrupadas em um único registro por
            edição. Mídia excluída na edição continua visível na Auditoria, para consulta futura.
          </p>
          <p>
            Quem pode editar <strong>itens</strong> e quem pode editar <strong>mídias</strong> são
            permissões <strong>separadas</strong>, definidas pelo administrador: é normal poder mexer em uma
            coisa e não na outra. As observações gerais seguem a permissão de <strong>itens</strong>,
            mesmo na saída. Não é possível acrescentar nem remover itens do checklist — só alterar as
            respostas dos que já existem.
          </p>
        </>
      ),
    },
    {
      titulo: 'PDF e envio ao cliente',
      conteudo: (
        <p>
          O botão de <strong>PDF</strong> gera o documento com os dados do veículo, as respostas, as
          observações e as fotos; quando já existe checklist de saída, entrada e saída saem no mesmo
          arquivo. O botão do <strong>WhatsApp</strong> abre a conversa com o proprietário e um link para
          ele consultar o checklist — o link exige que o cliente digite o <strong>telefone cadastrado</strong>{' '}
          e vale por 30 dias. Sem telefone no cadastro do veículo, o compartilhamento fica indisponível.
          <br />
          <br />
          Ao concluir a <strong>saída</strong>, o sistema lembra de enviar a{' '}
          <strong>Pesquisa de Satisfação</strong>: ela fica na aba <strong>Entregues</strong>, na placa do
          veículo, e a coluna <strong>Pesquisa</strong> daquela aba mostra quais atendimentos ainda não a
          receberam.
        </p>
      ),
    },
    {
      titulo: 'Se a tela recarregar',
      conteudo: (
        <p>
          Quando sai uma versão nova do sistema, a tela se atualiza sozinha. O que você estava preenchendo é
          guardado enquanto você preenche: as respostas e observações no sistema, e as fotos ainda não enviadas
          neste navegador. Na <strong>Recepção</strong>, um aviso oferece <strong>Retomar</strong> o checklist, e
          ele abre como estava. As fotos guardadas só voltam no mesmo computador; trocar de computador ou limpar
          os dados do navegador as perde. Salvar ou abandonar o checklist apaga o que foi guardado.
        </p>
      ),
    },
  ];

  /**
   * Barra da exclusão em massa — as caixas de seleção estão sempre nas thumbs;
   * a barra de ação só aparece quando há algo marcado (sem marcação, fica só a
   * hint explicando o recurso).
   *
   * Aparece no topo E no rodapé da lista (pedido do usuário, 06/08/2026): com
   * muitos ângulos, quem termina de marcar lá embaixo teria de rolar de volta
   * até o topo para excluir. O rodapé repete só a barra — a hint é texto de
   * aprendizado e cabe uma vez.
   */
  const renderSelecaoBarra = (posicao: 'topo' | 'rodape' = 'topo') => {
    if (!temMidiaSelecionavel) return null;

    if (selectedMediaIds.size === 0) {
      if (posicao === 'rodape') return null;
      return (
        <p className="checklist-selecao-hint">
          Para excluir várias mídias de uma vez, marque as caixas de seleção nas miniaturas — a exclusão
          pede uma única confirmação e a seleção vale para as duas abas de fotos.
        </p>
      );
    }

    return (
      <>
        <div className={`checklist-selecao-barra${posicao === 'rodape' ? ' rodape' : ''}`}>
          <span className="checklist-selecao-contagem">
            {selectedMediaIds.size} mídia(s) selecionada(s)
          </span>
          <button
            type="button"
            className="checklist-btn danger"
            disabled={loading}
            onClick={handleDeleteSelected}
          >
            Excluir selecionadas ({selectedMediaIds.size})
          </button>
          <button
            type="button"
            className="checklist-btn secondary"
            disabled={loading}
            onClick={limparSelecao}
          >
            Limpar seleção
          </button>
        </div>
        {posicao === 'topo' && (
          <p className="checklist-selecao-hint">
            A seleção vale para as duas abas de fotos: dá para marcar aqui, trocar de aba e excluir tudo junto.
          </p>
        )}
      </>
    );
  };

  /**
   * Campo de observações gerais da sessão (at-50). Em somente leitura exibe o
   * texto salvo (ou nada, quando a sessão não tem observação); editável segue a
   * permissão de itens, com limite de 10.000 caracteres.
   */
  const renderObservationField = () => {
    if (observationDisabled) {
      if (!observation.trim()) return null;
      return (
        <div className="checklist-observation">
          <div className="checklist-observation-title">{observationLabel}</div>
          <div className="checklist-observation-text">{observation}</div>
        </div>
      );
    }

    return (
      <div className="checklist-observation">
        <div className="checklist-observation-title">{observationLabel}</div>
        <p className="checklist-observation-hint">
          Campo livre e opcional para o que não cabe nos itens (ex: combinados com o cliente, pendências).
        </p>
        <textarea
          className="checklist-observation-input"
          value={observation}
          maxLength={OBSERVATION_MAX_LENGTH}
          disabled={loading}
          placeholder="Digite as observações, se houver."
          onChange={(e) => setObservation(e.target.value)}
        />
        <div className="checklist-observation-counter">
          {observation.length}/{OBSERVATION_MAX_LENGTH}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const showSaveNewChecklist = !readOnly && !isViewingSession && !entradaMissing;
  const showEditControls = isViewingSession && canEnterEditMode;
  // at-60: com carga incompleta, salvar registraria em cima de dados que a tela
  // não conseguiu mostrar — e o checklist da família entrada não pode ser refeito.
  const saveBlockedByLoadError = Boolean(loadError);

  return (
    <>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Checklist"
        secoes={helpSections}
      />
      {loading && (
        <FullScreenLoader
          progress={uploadProgress}
          message={uploadMessage}
        />
      )}
      <div className="edit-car-modal-overlay checklist-modal-specific">
        <div
          className="edit-car-modal-content"
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-label="Checklist"
        >
          <div className="Lightbox">
            <div className="LightboxLogin">
              {isInitialLoading ? (
                <div className="checklist-initial-loading">
                  <div className="checklist-spinner" />
                  <span>Carregando checklist...</span>
                </div>
              ) : (
              <>
              <div className="checklist-header">
                <div className="checklist-header-info">
                  <div className="EditarCarro">
                    Checklist - {checklistTypeLabel[checklistType] || checklistType}
                    {isEditing ? ' (editando)' : ''}
                    <button
                      type="button"
                      className="help-circle-btn"
                      title="Como Funciona"
                      aria-label="Como funciona o checklist"
                      onClick={() => setIsHelpOpen(true)}
                    >
                      ?
                    </button>
                  </div>
                  <div className="checklist-header-vehicle">
                    <span className="vehicle-model">{carInfo?.model || 'Veículo'}</span>
                    <span className="vehicle-divider">•</span>
                    <span className="vehicle-plate">Placa: <strong>{carInfo?.plate || '-'}</strong></span>
                    <span className="vehicle-divider">•</span>
                    <span className="vehicle-status">Status: <strong>{carInfo?.status || '-'}</strong></span>
                  </div>
                </div>
                {isViewingSession && !isEditing && (
                  <button
                    type="button"
                    className="checklist-whatsapp-btn"
                    disabled={loading}
                    aria-label="Compartilhar checklist via WhatsApp"
                    title={carInfo?.ownerPhone
                      ? 'Enviar link do checklist via WhatsApp'
                      : 'Cadastre o telefone do proprietário na edição do veículo para compartilhar'}
                    onClick={() => {
                      if (!carInfo?.ownerPhone) {
                        notify.warning('Este veículo não tem telefone do proprietário cadastrado — cadastre-o na edição do veículo para compartilhar.');
                        return;
                      }
                      void handleShareWhatsApp();
                    }}
                  >
                    <img src={whatsappIcon} alt="WhatsApp" />
                  </button>
                )}
              </div>

              {!entradaMissing && (
              <ChecklistTabs
                checklistType={effectiveType}
                active={activeTab}
                onChange={setActiveTab}
                status={tabsStatus}
                showService={isSaidaContext || serviceCount > 0}
              />
              )}

              <div className="checklist-body">
                {configError && <div className="checklist-error">{configError}</div>}

                {/*
                  at-96 — o comparativo. Só na vistoria de ENTRADA de um pós-evento: é ali que
                  alguém precisa comparar como o carro saiu com como ele voltou. Na saída do
                  próprio retorno o bloco não ajuda e só ocuparia espaço.
                */}
                {carInfo?.posEvento && ehFamiliaEntrada(effectiveType) && (
                  carInfo.posEvento.origemCarId ? (
                    <ComparativoSaidaOriginal
                      origemCarId={carInfo.posEvento.origemCarId}
                      origemEntregueEm={carInfo.posEvento.origemEntregueEm}
                    />
                  ) : (
                    /*
                      🔴 at-106: sem atendimento de origem NÃO existe checklist de saída para
                      comparar, e o bloco não pode ser montado — ele cairia no estado "o serviço
                      original saiu sem vistoria", que é uma afirmação sobre um serviço NOSSO.
                      Aqui o serviço não foi nosso, e dizer isso é a informação que quem preenche
                      a vistoria precisa para não procurar um histórico inexistente.
                    */
                    <div className="comparativo-sem-origem">
                      O serviço original deste retorno não está no sistema, então não há vistoria
                      anterior para comparar.
                      {carInfo.posEvento.origemEventNumber !== null &&
                        ` Ele está registrado pelo evento ${carInfo.posEvento.origemEventNumber}.`}
                    </div>
                  )
                )}

                {loadError && <div className="checklist-error">{loadError}</div>}

                {rascunhoRestauradoEm !== null && (
                  <RascunhoRestaurado
                    atualizadoEm={rascunhoRestauradoEm}
                    onComecarDoZero={createdSessionIdRef.current ? undefined : comecarChecklistDoZero}
                  />
                )}

                {entradaMissing && (
                  <div className="checklist-error">
                    O checklist de saída só pode ser iniciado após a conclusão do checklist de entrada deste veículo.
                  </div>
                )}

                {!entradaMissing && activeTab === 'ANGLES' && (
                  <div className="checklist-section">
                    <div className="checklist-section-title">Fotos</div>
                    <div className="checklist-section-desc">
                      {isSaidaContext
                        ? 'Registro do checklist de entrada (somente leitura).'
                        : 'Registre fotos/vídeos por posição — nenhuma é obrigatória. PDF anexado vira uma imagem por página.'}
                    </div>

                    {/* at-96: a pergunta de avarias é da vistoria de ENTRADA — vale igual no
                        pós-evento, então a checagem é por família. */}
                    {ehFamiliaEntrada(checklistType) && !isSaidaContext && (
                      <label className="checklist-toggle">
                        <input
                          type="checkbox"
                          checked={hasDamages}
                          onChange={(e) => setHasDamages(e.target.checked)}
                          disabled={loading || (isViewingSession ? true : readOnly)}
                        />
                        <span>Há avarias visíveis?</span>
                      </label>
                    )}
                    {isViewingSession && hasDamages && (
                      <div className="checklist-section-desc">Registrado na entrada: há avarias visíveis.</div>
                    )}

                    <div className="angles-progress">
                      <strong>{filledAngleSlots}/{angleConfigs.length}</strong> posições preenchidas
                    </div>

                    {!entradaMediaDisabled && renderSelecaoBarra()}

                    <div className="angles-slots-grid">
                      {angleConfigs.map((angle) => {
                        const slotKey = angle.key;
                        const items = angleCardItems(slotKey);
                        const hasAny = items.length > 0;

                        const angleStatusClass = hasAny
                          ? 'angle-slot-status angle-slot-status--ok'
                          : 'angle-slot-status angle-slot-status--idle';
                        const angleStatusLabel = hasAny ? 'Preenchido' : 'Sem fotos';

                        return (
                          <div
                            key={slotKey}
                            className="angle-slot-card"
                          >
                            <div className="angle-slot-card-head">
                              <div>
                                <div className="angle-slot-title">{angle.label}</div>
                                <div className="angle-slot-subtitle">{angle.helpText || ''}</div>
                              </div>
                              <div className={angleStatusClass}>{angleStatusLabel}</div>
                            </div>

                            {!entradaMediaDisabled && (
                              <div className="angle-slot-actions">
                                <button
                                  type="button"
                                  className="checklist-btn secondary"
                                  disabled={loading}
                                  onClick={() => {
                                    const input = document.getElementById(`angle-input-${slotKey}`) as HTMLInputElement | null;
                                    input?.click();
                                  }}
                                >
                                  {hasAny ? 'Adicionar' : 'Adicionar mídia'}
                                </button>

                                {hasAny && (
                                  <>
                                    <button
                                      type="button"
                                      className="checklist-btn secondary"
                                      disabled={loading}
                                      onClick={() => {
                                        const input = document.getElementById(`angle-replace-input-${slotKey}`) as HTMLInputElement | null;
                                        input?.click();
                                      }}
                                    >
                                      Trocar
                                    </button>

                                    <button
                                      type="button"
                                      className="checklist-btn secondary"
                                      disabled={loading}
                                      onClick={() => {
                                        openConfirm(`Remover todas as mídias de “${angle.label}”?`, async () => {
                                          closeConfirm();
                                          setLoading(true);
                                          setError(null);
                                          try {
                                            setAnglesLocalBySlot((prev) => ({ ...prev, [slotKey]: [] }));
                                            const uploaded = anglesUploadedBySlot[slotKey] || [];
                                            if (uploaded.length > 0) {
                                              // at-58: uma chamada para o slot inteiro (antes: um delete por mídia)
                                              await deleteChecklistMediaBatch(
                                                uploaded.map((m: any) => String(m.id)),
                                                currentEditBatchId(),
                                              );
                                              setAnglesUploadedBySlot((prev) => ({ ...prev, [slotKey]: [] }));
                                            }
                                            notify.success(`Mídias de “${angle.label}” removidas.`);
                                          } catch (e: any) {
                                            setError(e?.message || 'Não foi possível remover as mídias do slot.');
                                          } finally {
                                            setLoading(false);
                                          }
                                        });
                                      }}
                                    >
                                      Remover
                                    </button>
                                  </>
                                )}
                              </div>
                            )}

                            {!entradaMediaDisabled && (
                              <input
                                id={`angle-input-${slotKey}`}
                                className="checklist-file-input"
                                type="file"
                                multiple
                                accept="image/*,video/*,application/pdf"
                                onChange={(e) => {
                                  const files = Array.from(e.target.files || []);
                                  if (files.length > 0) {
                                    void addAngleFiles(slotKey, files);
                                  }
                                  e.currentTarget.value = '';
                                }}
                                disabled={loading}
                              />
                            )}

                            {!entradaMediaDisabled && (
                              <input
                                id={`angle-replace-input-${slotKey}`}
                                className="checklist-file-input"
                                type="file"
                                multiple
                                accept="image/*,video/*,application/pdf"
                                onChange={(e) => {
                                  const files = Array.from(e.target.files || []);
                                  if (files.length > 0) {
                                    void replaceAngleFiles(slotKey, files);
                                  }
                                  e.currentTarget.value = '';
                                }}
                                disabled={loading}
                              />
                            )}

                            <div className="angle-slot-grid">
                              <MediaGrid
                                items={items}
                                disabled={loading || entradaMediaDisabled}
                                onRemove={(it) => removeAngleItem(slotKey, it)}
                                selectable={!entradaMediaDisabled}
                                selectedIds={selectedMediaIds}
                                onToggleSelect={toggleMediaSelection}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Seções personalizadas de fotos (nome livre, ex: "Pneus") */}
                    {(customSectionNames.length > 0 || !entradaMediaDisabled) && (
                      <div className="checklist-custom-sections" style={{ marginTop: 20 }}>
                        <div className="checklist-section-title">Seções personalizadas</div>
                        <div className="checklist-section-desc">
                          Crie seções com nome livre para agrupar fotos/vídeos extras (ex: Pneus, Motor).
                        </div>

                        {!entradaMediaDisabled && (
                          <div style={{ display: 'flex', gap: 8, margin: '8px 0 12px' }}>
                            <input
                              type="text"
                              className="Input"
                              placeholder="Nome da seção (ex: Pneus)"
                              value={newSectionName}
                              maxLength={50}
                              onChange={(e) => setNewSectionName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  createCustomSection();
                                }
                              }}
                              disabled={loading}
                              style={{ maxWidth: 280 }}
                            />
                            <button
                              type="button"
                              className="checklist-btn secondary"
                              disabled={loading}
                              onClick={createCustomSection}
                            >
                              Criar seção
                            </button>
                          </div>
                        )}

                        {customSectionNames.map((sectionName) => {
                          const items = customCardItems(sectionName);
                          return (
                            <div key={sectionName} className="angle-slot-card" style={{ marginBottom: 12 }}>
                              <div className="angle-slot-card-head">
                                <div>
                                  <div className="angle-slot-title">{sectionName}</div>
                                  <div className="angle-slot-subtitle">Seção personalizada</div>
                                </div>
                                <div className={items.length > 0 ? 'angle-slot-status angle-slot-status--ok' : 'angle-slot-status angle-slot-status--idle'}>
                                  {items.length} mídia(s)
                                </div>
                              </div>

                              {!entradaMediaDisabled && (
                                <div className="angle-slot-actions">
                                  <button
                                    type="button"
                                    className="checklist-btn secondary"
                                    disabled={loading}
                                    onClick={() => {
                                      const input = document.getElementById(`custom-input-${sectionName}`) as HTMLInputElement | null;
                                      input?.click();
                                    }}
                                  >
                                    Adicionar mídia
                                  </button>
                                  <input
                                    id={`custom-input-${sectionName}`}
                                    className="checklist-file-input"
                                    type="file"
                                    multiple
                                    accept="image/*,video/*,application/pdf"
                                    onChange={(e) => {
                                      const files = Array.from(e.target.files || []);
                                      if (files.length > 0) {
                                        void addCustomFiles(sectionName, files);
                                      }
                                      e.currentTarget.value = '';
                                    }}
                                    disabled={loading}
                                  />
                                </div>
                              )}

                              <div className="angle-slot-grid">
                                <MediaGrid
                                  items={items}
                                  disabled={loading || entradaMediaDisabled}
                                  onRemove={(it) => removeCustomItem(sectionName, it)}
                                  selectable={!entradaMediaDisabled}
                                  selectedIds={selectedMediaIds}
                                  onToggleSelect={toggleMediaSelection}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Repetida no fim da lista: quem marca lá embaixo não precisa rolar de volta */}
                    {!entradaMediaDisabled && renderSelecaoBarra('rodape')}
                  </div>
                )}

                {!entradaMissing && activeTab === 'SERVICE' && (isSaidaContext || serviceCount > 0) && (
                  <div className="checklist-section">
                    <div className="checklist-section-title">Imagens de saída</div>
                    <div className="checklist-section-desc">
                      Anexe fotos/vídeos do veículo na saída (ex: serviço concluído, estado de entrega).
                    </div>

                    {!serviceMediaDisabled && (
                      <MediaUploader
                        category="SERVICE"
                        disabled={loading}
                        accept="image/*,video/*,application/pdf"
                        onAddFiles={(files) => { void addServiceFiles(files); }}
                        helperText="Fotos e vídeos são opcionais. PDF anexado vira uma imagem por página."
                      />
                    )}

                    {!serviceMediaDisabled && renderSelecaoBarra()}

                    <MediaGrid
                      items={serviceGridItems}
                      disabled={loading || serviceMediaDisabled}
                      onRemove={(it) => removeServiceItem(it)}
                      selectable={!serviceMediaDisabled}
                      selectedIds={selectedMediaIds}
                      onToggleSelect={toggleMediaSelection}
                    />

                    {!serviceMediaDisabled && renderSelecaoBarra('rodape')}

                    {/* at-50: observações da saída ficam junto das imagens de saída */}
                    {isSaidaContext && renderObservationField()}
                  </div>
                )}

                {!entradaMissing && activeTab === 'ITEMS' && (
                  <>
                    {isSaidaContext && (
                      <div className="checklist-section-desc">
                        Itens respondidos no checklist de entrada (somente leitura).
                      </div>
                    )}
                    <ChecklistItemsTab
                      templates={itemTemplates}
                      answers={itemAnswers}
                      disabled={loading || entradaItemsDisabled}
                      onStatusChange={updateItemStatus}
                      onNoteChange={updateItemNote}
                      onMarkAllOk={markAllItemsOk}
                      onClear={clearAllItems}
                      pendingByCategory={itemPendingByCategory}
                      readOnly={entradaItemsDisabled}
                    />

                    {/* at-50: observações gerais. No contexto de saída, as da entrada
                        aparecem aqui como referência somente leitura. */}
                    {isSaidaContext ? (
                      entradaObservation ? (
                        <div className="checklist-observation">
                          <div className="checklist-observation-title">Observações de Entrada</div>
                          <div className="checklist-observation-text">{entradaObservation}</div>
                        </div>
                      ) : null
                    ) : (
                      renderObservationField()
                    )}
                  </>
                )}

                {error && <div className="checklist-error">{error}</div>}
              </div>

              <div className="Frame1000003998">
                <div className="CtaBigEditar cancel" onClick={requestClose}>
                  <button className="CustomFileCancel" type="button" disabled={loading}>
                    {showSaveNewChecklist || isEditing ? 'Cancelar' : 'Fechar'}
                  </button>
                </div>

                {isViewingSession && !isEditing && (
                  <button
                    className="CtaBigEditar save"
                    type="button"
                    disabled={loading}
                    onClick={handleDownloadPdf}
                  >
                    Baixar PDF
                  </button>
                )}

                {showEditControls && !isEditing && (
                  <button
                    className="CtaBigEditar save"
                    type="button"
                    disabled={loading || !config || saveBlockedByLoadError}
                    title={saveBlockedByLoadError ? 'Recarregue a página: o checklist não foi carregado por completo.' : undefined}
                    onClick={() => {
                      // Cada entrada no modo edição é um lote novo na Auditoria
                      editBatchIdRef.current =
                        typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function'
                          ? (crypto as any).randomUUID()
                          : `edit-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                      setIsEditing(true);
                    }}
                  >
                    Editar checklist
                  </button>
                )}

                {showEditControls && isEditing && (
                  <button
                    className="CtaBigEditar save"
                    type="button"
                    disabled={loading || saveBlockedByLoadError}
                    title={saveBlockedByLoadError ? 'Recarregue a página: o checklist não foi carregado por completo.' : undefined}
                    onClick={handleSaveSessionEdits}
                  >
                    {loading ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                )}

                {showSaveNewChecklist && (
                  <button
                    className="CtaBigEditar save"
                    type="button"
                    disabled={loading || !config || saveBlockedByLoadError}
                    title={saveBlockedByLoadError ? 'Recarregue a página: o checklist não foi carregado por completo.' : undefined}
                    onClick={async () => {
                      if (!validateChecklist()) return;

                      const warnings = missingPhotoWarnings();
                      const warningLine = warnings.length > 0
                        ? `Você está salvando sem fotos de: ${warnings.join(' e ')}.\n\n`
                        : '';

                      if (ehFamiliaSaida(checklistType)) {
                        // at-46: a pendência vem do ESTADO do serviço (helper único), separando
                        // "ficou inacabado" de "nunca foi realizado". Antes tudo era inferido de
                        // período APPROVED, e serviço pausado passava como realizado.
                        // at-60: a consulta pode falhar (403, 500, rede). Nesse caso o helper
                        // devolve `verificacaoIndisponivel` em vez de listas vazias, e a
                        // confirmação diz que não deu para verificar — antes o texto ficava
                        // sem nenhuma linha de pendência, o que o usuário lê como "está tudo
                        // concluído" bem no passo que encerra o atendimento.
                        const pendentes = await buscarServicosPendentes(carId);
                        const partes: string[] = [];
                        if (pendentes.verificacaoIndisponivel) {
                          partes.push('Não foi possível verificar se este veículo tem serviços em aberto. Confira a situação do veículo antes de concluir');
                        }
                        if (pendentes.inacabados.length > 0) {
                          partes.push(`Serviços que ficaram inacabados: ${pendentes.inacabados.join(', ')}`);
                        }
                        if (pendentes.naoRealizadas.length > 0) {
                          partes.push(`Etapas planejadas não realizadas: ${pendentes.naoRealizadas.join(', ')}`);
                        }
                        const pendingLine = partes.length > 0 ? `\n\n${partes.join('. ')}.` : '';
                        openConfirm(
                          `${warningLine}Ao concluir o checklist de saída, o veículo será movido para a esteira Entrega, e sairá do fluxo da oficina.${pendingLine}\n\nDeseja continuar?`,
                          () => {
                            closeConfirm();
                            void handleSaveChecklist();
                          },
                          'Confirmar',
                        );
                      } else {
                        openConfirm(
                          `${warningLine}Tem certeza de que deseja concluir o checklist de ${checklistTypeLabel[checklistType] || checklistType} do veículo ${carInfo?.plate || ''}?`,
                          () => {
                            closeConfirm();
                            void handleSaveChecklist();
                          },
                          'Concluir',
                        );
                      }
                    }}
                  >
                    {loading ? 'Salvando...' : 'Salvar checklist'}
                  </button>
                )}
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChecklistModal;
